import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { set, get } from 'idb-keyval';
import { ref, onValue, set as firebaseSet, off, get as firebaseGet } from 'firebase/database';
import { db } from './firebase';
import Toast from './components/Toast';
import Login from './components/Login';
import InstallBanner from './components/InstallBanner';

const Operations = lazy(() => import('./components/Operations'));
const Datalogger = lazy(() => import('./components/Datalogger'));
const Waybill = lazy(() => import('./components/Waybill'));
const STOs = lazy(() => import('./components/STOs'));
const Admin = lazy(() => import('./components/Admin'));
const ChangePasswordModal = lazy(() => import('./components/ChangePasswordModal'));

const TabSkeletonLoader = () => (
  <div className="w-full space-y-4 animate-pulse max-w-7xl mx-auto">
    <div className="bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex justify-between items-center">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-200 dark:bg-gray-700 rounded-xl"></div>
        <div className="space-y-2">
          <div className="h-4 w-32 bg-slate-200 dark:bg-gray-700 rounded"></div>
          <div className="h-3 w-24 bg-slate-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="h-8 w-24 bg-slate-200 dark:bg-gray-700 rounded-xl"></div>
        <div className="h-8 w-20 bg-slate-200 dark:bg-gray-700 rounded-xl"></div>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="h-3 w-20 bg-slate-200 dark:bg-gray-700 rounded"></div>
              <div className="h-6 w-16 bg-slate-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="w-8 h-8 bg-slate-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      ))}
    </div>

    <div className="bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
      <div className="h-10 w-full bg-slate-100 dark:bg-gray-700/60 rounded-xl"></div>
      <div className="space-y-2.5 pt-2">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-12 w-full bg-slate-50 dark:bg-gray-700/40 rounded-xl border border-gray-100 dark:border-gray-700/50"></div>
        ))}
      </div>
    </div>
  </div>
);

export const APP_VERSION = 'v3.1';
const VERSION_STORAGE_KEY = 'dltrack_app_version';
const VERSION_TIMESTAMP_KEY = 'dltrack_version_timestamp';
const VERSION_NEW_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function App() {
  const [isNewVersion, setIsNewVersion] = useState(() => {
    try {
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(VERSION_TIMESTAMP_KEY);
      const now = Date.now();

      // Whenever the version string changes, update stored version and start a fresh 24hr window
      if (storedVersion !== APP_VERSION) {
        localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION);
        localStorage.setItem(VERSION_TIMESTAMP_KEY, now.toString());
        return true;
      }

      if (storedTimestamp) {
        const elapsed = now - Number(storedTimestamp);
        return elapsed >= 0 && elapsed < VERSION_NEW_DURATION_MS;
      }

      return false;
    } catch {
      return false;
    }
  });

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem('logger_session');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [activeTab, setActiveTab] = useState(null);
  const [operationsSubTab, setOperationsSubTab] = useState('inbound');
  const [initialScannerState, setInitialScannerState] = useState(null);
  const [isTabLoading, setIsTabLoading] = useState(true);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [isTabSyncing, setIsTabSyncing] = useState(false);
  const tabSyncEnabled = useRef(true);
  const lastTabChangeSource = useRef('remote');

  useEffect(() => {
    get('theme').then((val) => {
      if (val === 'dark') {
        setDarkMode(true);
        document.documentElement.classList.add('dark');
      }
    });
  }, []);

  // Auto-hide the "NEW" version tag once the 24-hour window expires
  useEffect(() => {
    if (!isNewVersion) return;
    try {
      const storedTimestamp = Number(localStorage.getItem(VERSION_TIMESTAMP_KEY)) || Date.now();
      const remainingTime = VERSION_NEW_DURATION_MS - (Date.now() - storedTimestamp);

      if (remainingTime <= 0) {
        setIsNewVersion(false);
        return;
      }

      const timer = setTimeout(() => {
        setIsNewVersion(false);
      }, remainingTime);

      return () => clearTimeout(timer);
    } catch {
      // ignore
    }
  }, [isNewVersion]);

  // Load tab & scanner subTab data on user login - load directly from Firebase during skeleton loading
  useEffect(() => {
    if (!currentUser) {
      setIsTabLoading(false);
      return;
    }

    const userTabRef = ref(db, `users/${currentUser.companyID}/activeTab`);
    const scannerStateRef = ref(db, `users/${currentUser.companyID}/scannerState`);

    Promise.all([
      firebaseGet(userTabRef),
      firebaseGet(scannerStateRef)
    ]).then(([tabSnap, scannerSnap]) => {
      const tabData = tabSnap.val();
      if (tabData && tabData.tab && ['dashboard', 'scanner', 'waybill', 'stos', 'admin'].includes(tabData.tab)) {
        setActiveTab(tabData.tab);
      } else {
        setActiveTab('scanner');
      }

      const scannerData = scannerSnap.val();
      if (scannerData) {
        setInitialScannerState(scannerData);
        if (scannerData.subTab && ['inbound', 'outbound', 'return'].includes(scannerData.subTab)) {
          setOperationsSubTab(scannerData.subTab);
        }
      }

      setIsTabLoading(false);
    }).catch(() => {
      setActiveTab('scanner');
      setIsTabLoading(false);
    });
  }, [currentUser]);

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newMode = !prev;
      if (newMode) {
        document.documentElement.classList.add('dark');
        set('theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        set('theme', 'light');
      }
      return newMode;
    });
  };

  // Tab synchronization effect
  useEffect(() => {
    if (!currentUser) return;

    const userTabRef = ref(db, `users/${currentUser.companyID}/activeTab`);

    // Set up real-time listener for tab changes
    const unsubscribe = onValue(userTabRef, (snapshot) => {
      const tabData = snapshot.val();
      if (tabData && tabData.tab && tabSyncEnabled.current) {
        if (lastTabChangeSource.current !== 'local' && tabData.tab !== activeTab) {
          setIsTabSyncing(true);
          setActiveTab(tabData.tab);
          setTimeout(() => setIsTabSyncing(false), 400);
        }
      }
    });

    return () => {
      off(userTabRef, 'value', unsubscribe);
    };
  }, [currentUser, activeTab]);

  // Sync tab changes to Firebase
  const syncTabChange = (tab) => {
    if (!currentUser || !tabSyncEnabled.current) return;

    lastTabChangeSource.current = 'local';
    const userTabRef = ref(db, `users/${currentUser.companyID}/activeTab`);
    firebaseSet(userTabRef, {
      tab: tab,
      timestamp: Date.now()
    });

    // Reset source tracking after a delay
    setTimeout(() => {
      lastTabChangeSource.current = 'remote';
    }, 1000);
  };

  // Enhanced setActiveTab with sync
  const handleTabChange = (tab) => {
    // Only change if it's different from current tab
    if (activeTab !== tab) {
      setActiveTab(tab);
      syncTabChange(tab);
    }
  };

  const handleLogin = (user) => {
    localStorage.setItem('logger_session', JSON.stringify(user));
    setCurrentUser(user);
    showToast(`Welcome back, ${user.name}`, 'success');
  };

  const handleLogout = () => {
    localStorage.removeItem('logger_session');
    setCurrentUser(null);
  };

  const showToast = (message, type = 'info', options = {}) => {
    setToast({
      message,
      type: typeof type === 'string' ? type : 'info',
      title: options?.title,
      confirmText: options?.confirmText,
      dismissText: options?.dismissText,
      cancelText: options?.cancelText,
      showCancel: options?.showCancel,
      onConfirm: options?.onConfirm,
      onCancel: options?.onCancel,
      duration: options?.duration,
    });
  };

  if (!currentUser) {
    return (
      <div>
        <Login onLogin={handleLogin} />
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            title={toast.title}
            confirmText={toast.confirmText}
            dismissText={toast.dismissText}
            cancelText={toast.cancelText}
            showCancel={toast.showCancel}
            onConfirm={toast.onConfirm}
            onCancel={toast.onCancel}
            duration={toast.duration}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">

      <header className="bg-[#1e293b] dark:bg-black text-white shadow-md relative z-30 transition-colors no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">

          <div className="flex items-center space-x-3">

            <img
              src="/img/logo.png"
              alt="Logo"
              className="h-10 w-auto rounded object-contain bg-transparent dark:bg-transparent p-0.5"
              onError={(e) => {
                e.target.style.display = 'none';
                document.getElementById('header-fallback-icon').style.display = 'flex';
              }}
            />
            <div id="header-fallback-icon" className="bg-blue-500/20 p-2 rounded-lg text-blue-400" style={{ display: 'none' }}>
              <i className="fa-solid fa-box-archive text-xl"></i>
            </div>

            <span className="font-bold text-xl tracking-wide">AFGL Logistic Hub</span>
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4">
            <button onClick={toggleDarkMode} className="text-slate-300 hover:text-white px-2 py-2 rounded-lg text-lg transition-all mr-1 sm:mr-2">
              <i className={`fa-solid ${darkMode ? 'fa-sun text-yellow-400' : 'fa-moon'}`}></i>
            </button>

            <button
              onClick={() => setIsChangePasswordOpen(true)}
              className="flex flex-col items-end text-right px-2.5 py-1 rounded-xl hover:bg-slate-700/60 transition-all group cursor-pointer border border-transparent hover:border-slate-600/60"
              title="Click to Change Password"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-slate-200 leading-tight group-hover:text-blue-300 transition-colors">{currentUser.name}</span>
                <i className="fa-solid fa-key text-[10px] text-slate-400 group-hover:text-blue-300 transition-colors opacity-75"></i>
              </div>
              <span className="text-[10px] uppercase text-blue-300 font-bold tracking-wider">{currentUser.role}</span>
            </button>
            <button onClick={handleLogout} className="bg-slate-700/50 hover:bg-red-500/90 text-slate-200 hover:text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm border border-slate-600/50 hover:border-red-500">
              <i className="fa-solid fa-right-from-bracket"></i> <span className="hidden sm:inline-block ml-1">Logout</span>
            </button>
          </div>

        </div>
      </header>

      <div className="bg-[#1e293b] dark:bg-black text-white sticky top-0 z-40 border-t border-slate-700/50 shadow-md shadow-slate-900/10 transition-colors no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between py-2">
            <div className="flex space-x-2 overflow-x-auto no-scrollbar">
              <button onClick={() => handleTabChange('scanner')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center ${activeTab === 'scanner' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-300 hover:text-white hover:bg-slate-700/50 dark:hover:bg-gray-800'}`}>
                <i className="fa-solid fa-qrcode mr-2"></i>Operations
              </button>
              <button onClick={() => handleTabChange('waybill')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center ${activeTab === 'waybill' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-300 hover:text-white hover:bg-slate-700/50 dark:hover:bg-gray-800'}`}>
                <i className="fa-solid fa-file-invoice mr-2"></i>Waybill
              </button>
              <button onClick={() => handleTabChange('stos')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center ${activeTab === 'stos' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-300 hover:text-white hover:bg-slate-700/50 dark:hover:bg-gray-800'}`}>
                <i className="fa-solid fa-boxes-packing mr-2"></i>STOs
              </button>
              <button onClick={() => handleTabChange('dashboard')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center ${activeTab === 'dashboard' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-300 hover:text-white hover:bg-slate-700/50 dark:hover:bg-gray-800'}`}>
                <i className="fa-solid fa-temperature-half mr-2"></i>Datalogger
              </button>
              {currentUser.role === 'admin' && (
                <button onClick={() => handleTabChange('admin')} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center ${activeTab === 'admin' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-300 hover:text-white hover:bg-slate-700/50 dark:hover:bg-gray-800'}`}>
                  <i className="fa-solid fa-shield-halved mr-2"></i>Admin
                </button>
              )}
            </div>
            {isTabSyncing && (
              <div className="flex items-center text-xs text-green-400 ml-4 animate-pulse">
                <i className="fa-solid fa-sync-alt mr-1"></i>
                Syncing...
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="flex-1 p-4 sm:p-6 max-w-7xl w-full mx-auto pb-8 sm:pb-10">
        {isTabLoading ? (
          <TabSkeletonLoader />
        ) : (
          <Suspense fallback={<TabSkeletonLoader />}>
            <div style={{ display: activeTab === 'scanner' ? 'block' : 'none' }} className="animate-fadein">
              <Operations
                currentUser={currentUser}
                showToast={showToast}
                activeSubTab={operationsSubTab}
                onSubTabChange={setOperationsSubTab}
                initialState={initialScannerState}
              />
            </div>
            <div style={{ display: activeTab === 'waybill' ? 'block' : 'none' }} className="animate-fadein">
              <Waybill
                currentUser={currentUser}
                showToast={showToast}
                navigateToOperations={(subTab = 'outbound') => {
                  setOperationsSubTab(subTab);
                  handleTabChange('scanner');
                }}
              />
            </div>
            <div style={{ display: activeTab === 'stos' ? 'block' : 'none' }} className="animate-fadein">
              <STOs showToast={showToast} currentUser={currentUser} />
            </div>
            <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }} className="animate-fadein">
              <Datalogger showToast={showToast} currentUser={currentUser} />
            </div>
            {currentUser.role === 'admin' && (
              <div style={{ display: activeTab === 'admin' ? 'block' : 'none' }} className="animate-fadein">
                <Admin showToast={showToast} currentUser={currentUser} />
              </div>
            )}
          </Suspense>
        )}
      </main>

      <footer className="sticky bottom-0 z-40 w-full mt-auto bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200/80 dark:border-gray-800/80 py-2.5 px-4 sm:px-6 flex flex-col sm:flex-row gap-2 justify-between items-center text-xs text-gray-500 dark:text-gray-400 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_16px_rgba(0,0,0,0.4)] transition-colors no-print">
        <div className="flex items-center">
          <span className="font-bold text-blue-600 dark:text-blue-500 text-[11px] sm:text-sm tracking-wide mr-1">AFGL</span> Logistic Hub
        </div>
        <div className="font-medium bg-gray-100 dark:bg-gray-800 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap inline-flex items-center">
          Developed by <span className="text-gray-800 dark:text-gray-200 font-semibold ml-1">Eng. Hassan Alzaki</span> <span className="text-blue-500 font-semibold ml-1">{APP_VERSION}</span>
          {isNewVersion && (
            <span
              className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500 text-white shadow-sm shadow-emerald-500/30 animate-pulse"
              title="New version"
            >
              NEW
            </span>
          )}
        </div>
        <div className="hidden sm:block uppercase font-semibold">
          © 2026 All Rights Reserved
        </div>
      </footer>
      <InstallBanner />
      {isChangePasswordOpen && (
        <Suspense fallback={null}>
          <ChangePasswordModal
            isOpen={isChangePasswordOpen}
            onClose={() => setIsChangePasswordOpen(false)}
            currentUser={currentUser}
            onPasswordUpdated={(updated) => setCurrentUser(updated)}
            showToast={showToast}
          />
        </Suspense>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          title={toast.title}
          confirmText={toast.confirmText}
          dismissText={toast.dismissText}
          cancelText={toast.cancelText}
          showCancel={toast.showCancel}
          onConfirm={toast.onConfirm}
          onCancel={toast.onCancel}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default App;
